import React, { useCallback } from 'react';
import { View } from 'react-native';
import { useAppSelector } from 'store/hooks';
import { Button, CardView, CurrencyAmountText, rem, Spacer } from 'design-system';
import { BORDER_RADIUS_SQUARE, HEIGHT_SQUARE } from 'design-system/Button';
import EmojiText from 'design-system/EmojiText';
import IconArchive from 'design-system/icons/IconArchive';
import IconTooltip from 'design-system/icons/IconTooltip';
import { SettingsItemIcon } from 'design-system/SettingsItemIcon';
import Text from 'design-system/Text';
import { muteAllButThrows, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { WithdrawalTimesInfoModal } from 'features/invest/components/WithdrawalTimesInfoModal';
import { useConnectedAccount } from 'features/invest/hooks/selectors';
import { reopenPot } from 'features/pots/actions';
import { InternalTransfer } from 'features/transfers/types';
import ISAAllowanceInfo from 'features/wealth/components/ISAAllowanceInfo';
import { selectAutosaveStatusObj } from 'reducers/selectors';
import { formatAmount } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { showUnavailableOnWeb } from 'utils/webGate';
import useDepositToPot from '../hooks/useDepositToPot';
import { selectActivePotInterest, selectPotProductFromProductId } from '../reducers/selectors';
import { showDeletedPotAlert } from '../utils';
import DepositToPotModal from './DepositToPotModal';
const renderTitle = (potProduct, userInterest, withdrawableBalance) => {
    if (potProduct && (potProduct?.type === 'INTEREST' || potProduct?.type === 'INTEREST_NOTICE') && userInterest) {
        if (withdrawableBalance) {
            return (<Text TextThin-16 centered Secondary>
          Available{' '}
          <Text NumbersThin-16 Secondary>
            {formatAmount(withdrawableBalance.value, withdrawableBalance.currency, 2)}
          </Text>
          {!!userInterest && (<Text TextThin-16 Secondary>
              {' • AER '}
              <Text NumbersThin-16 Secondary>
                {`${userInterest}%`}
              </Text>
            </Text>)}
        </Text>);
        }
        return (<Text TextThin-16 centered Secondary>
        {potProduct.name}
        {!!userInterest && (<Text TextThin-16 Secondary>
            {' • AER '}
            <Text NumbersThin-16 Secondary>
              {`${userInterest}%`}
            </Text>
          </Text>)}
      </Text>);
    }
    return (<Text TextThin-16 Secondary>
      Total Balance
    </Text>);
};
const PotScreenHeader = ({ pot, isDefaultSpace, isPotOwner }) => {
    const styles = useStyles(styleSet);
    const { navigateDeprecated, pop, navigate } = useMainStackNavigation();
    const isPaused = useAppSelector((state) => state.pots.savingsPlan?.isPaused);
    const savingsPlanMandateStatus = useAppSelector((state) => state.pots.savingsPlan?.mandateStatus);
    const potProduct = useAppSelector((store) => selectPotProductFromProductId(store, pot.productId));
    const activePotInterest = useAppSelector((store) => selectActivePotInterest(store, pot.productId));
    const autosaveStatusObj = useAppSelector(selectAutosaveStatusObj);
    const [, , , fetch] = useFetchOld(muteAllButThrows);
    const colors = useColors();
    const connectedAccount = useConnectedAccount();
    const handleDepositToPot = useDepositToPot(pot);
    const handleFund = useCallback(() => {
        if (pot.deletedAt) {
            showDeletedPotAlert();
            return;
        }
        if (showUnavailableOnWeb('emma://save?tab=pots', 'Add money')) {
            return;
        }
        if (isPaused || !savingsPlanMandateStatus || autosaveStatusObj?.actionRequired) {
            DepositToPotModal.show(pot);
            return;
        }
        handleDepositToPot();
    }, [pot, isPaused, savingsPlanMandateStatus, autosaveStatusObj?.actionRequired, handleDepositToPot]);
    const handleWithdraw = useCallback(() => {
        if (pot.deletedAt) {
            showDeletedPotAlert();
            return;
        }
        if (showUnavailableOnWeb('emma://save?tab=pots', 'Withdraw')) {
            return;
        }
        if (connectedAccount) {
            navigateDeprecated('Transfer', {
                from: pot,
                to: {
                    ...connectedAccount,
                    itemType: 'investConnectedAccount',
                },
                type: InternalTransfer.withdraw,
            });
        }
        else {
            navigate('SelectFundingAccount', {
                pot,
            });
        }
    }, [pot, connectedAccount]);
    const handleUnarchive = useCallback(() => Modal.showConfirmation({
        title: 'Unarchive this pot?',
        subtitle: 'It will return to your active pots.',
        confirmButtonTitle: 'No, thanks',
        cancelButtonTitle: 'Yes, unarchive',
        onPressCancel: async () => {
            try {
                pop(2);
                await fetch(reopenPot(pot.id));
            }
            catch {
                // nothing to do here, api middleware shows the error on its own
            }
        },
    }), [pot.id]);
    const showWithdrawalTimesInfo = useCallback(() => {
        if (potProduct?.withdrawalTimesInfo) {
            WithdrawalTimesInfoModal.show(potProduct?.withdrawalTimesInfo);
        }
    }, [potProduct?.withdrawalTimesInfo]);
    const isAccumulatedInterestPot = pot.type === 'ACCUMULATED_INTEREST' || pot.type === 'ACCUMULATED_INTEREST_NOTICE';
    return (<View style={styles.container}>
      <Spacer h={8}/>
      <View style={styles.header}>
        <View style={styles.emojiContainer}>
          <View style={styles.emojiContainerInner}>
            <EmojiText fontSize={rem(26)}>{pot.emoji}</EmojiText>
          </View>
        </View>
        <Spacer.H8 />
        <CurrencyAmountText size="XL" value={{ amount: pot.balance.value, currency: pot.balance.currency }}/>
        <Spacer h={2}/>
        {renderTitle(potProduct, activePotInterest, pot?.withdrawableBalance)}
      </View>
      {!!(isDefaultSpace || isPotOwner) &&
            (pot.type !== 'BASIC' ? (
            // Some products, like the Cash ISA, come without withdrawal times, so there is nothing to show
            !!potProduct?.withdrawalTimesInfo && (<>
              <Spacer h={16}/>
              <Button hug thin square transparentOnDark title="Withdrawal times" onPress={showWithdrawalTimesInfo} containerStyle={styles.withdrawalTimesBtnWrapper} rightIconContainerStyle={styles.rightIconContainerStyles} rightIcon={<IconTooltip colorKey="secondary" width={16} height={16}/>}/>
            </>)) : (<>
            <Spacer h={16}/>
            <View style={styles.instantWithdrawalView}>
              <Text TextThin-14 Secondary>
                Instant withdrawals
              </Text>
            </View>
          </>))}

      <Spacer h={32}/>

      {!!potProduct?.isProductCashISA && !isAccumulatedInterestPot && (<>
          <Spacer h={8}/>
          <ISAAllowanceInfo fromPots/>
          <Spacer h={8}/>
        </>)}

      {(isDefaultSpace || isPotOwner) && !pot.deletedAt ? (<View style={styles.btnContainer}>
          <Button flex small brandReversed title="Withdraw" onPress={handleWithdraw}/>
          {!isAccumulatedInterestPot && (<>
              <Spacer w={16}/>
              <Button flex small brand title="Deposit" onPress={handleFund}/>
            </>)}
        </View>) : null}

      {pot.deletedAt && !isAccumulatedInterestPot ? (<CardView withPadding noMargin style={styles.marginTop24}>
          <SettingsItemIcon>
            <IconArchive color={colors.elements.secondary}/>
          </SettingsItemIcon>
          <View style={styles.marginTop}>
            <Text Text-16>This pot is archived</Text>
            <Spacer h={4}/>
            <Text TextThin-14 Secondary>
              You archived this pot in the past. You can unarchive it at any time.
            </Text>
            <Button square brandReversed onPress={handleUnarchive} title="Unarchive" style={styles.archivedBtn}/>
          </View>
        </CardView>) : null}
    </View>);
};
export default PotScreenHeader;
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingHorizontal: rem(16),
    },
    btnContainer: {
        flexDirection: 'row',
        paddingVertical: rem(16),
    },
    header: {
        alignItems: 'center',
    },
    emojiContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    emojiContainerInner: {
        width: rem(52),
        height: rem(52),
        borderRadius: rem(52),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.dark,
    },
    withdrawalTimesBtnWrapper: {
        paddingHorizontal: rem(12),
    },
    instantWithdrawalView: {
        alignSelf: 'center',
        height: HEIGHT_SQUARE,
        justifyContent: 'center',
        paddingHorizontal: rem(12),
        borderRadius: BORDER_RADIUS_SQUARE,
        backgroundColor: colors.cards.transparentOnDark,
    },
    rightIconContainerStyles: {
        paddingLeft: rem(8),
    },
    marginTop: {
        marginTop: rem(16),
    },
    marginTop24: {
        marginTop: rem(24),
    },
    archivedBtn: {
        alignSelf: 'flex-start',
        marginTop: rem(16),
        paddingHorizontal: rem(8),
    },
}));
